import {
	AfterViewInit,
	Component,
	Inject,
	Input,
	OnDestroy,
	ViewChild,
} from '@angular/core';
import {
	MAT_DIALOG_DATA,
	MatDialog,
	MatDialogRef,
} from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { Device, DeviceGroupDeviceStatus } from 'src/app/models/data/device';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { ApiLoadingKeys, ApiService } from 'src/app/services/api.services';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { Mode } from 'src/app/models/model';
import {
	StatusDevice,
	DeviceStatusComponent,
} from '../device-status/device-status.component';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { Company } from 'src/app/models/data/company';
import { DeviceDetailsService } from 'src/app/services/device-details.service';
import { ENV } from 'src/environment.provider';
import { Environment } from 'src/environments/environment';
import { CancelConfirmationComponent } from 'src/app/components/cancel-confirmation/cancel-confirmation.component';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetPresentationToggleComponent,
	DataSetActionButtonComponent,
	DataSetSelectedActionGroupDirective,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import {
	DataSetTableComponent,
	DataSetTableRowSelectionComponent,
	DataSetTableMenuComponent,
	DataSetRowClickDirective,
} from '../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { HasSystemCapabilityDirective } from '../../../directives/system-capabilities/has-system-capability.directive';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import { DataSetMapViewComponent } from '../../../components/data-set/data-set-map.component';
import { NgPlural, NgPluralCase } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-device-group-devices',
	templateUrl: './device-group-devices.component.html',
	styleUrls: [
		'./device-group-devices.component.scss',
		'../../common/main.scss',
	],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		DataSetPrimaryActionGroupDirective,
		DataSetPresentationToggleComponent,
		DataSetActionButtonComponent,
		DataSetSelectedActionGroupDirective,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCellDef,
		MatCell,
		DataSetTableRowSelectionComponent,
		HasSystemCapabilityDirective,
		MatSortHeader,
		MatIcon,
		DeviceStatusComponent,
		DataSetTableMenuComponent,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DataSetRowClickDirective,
		MatMenu,
		MatMenuContent,
		MatMenuItem,
		DataSetMapViewComponent,
		DataSetLoadingRowComponent,
	],
})
export class DeviceGroupDevicesComponent implements AfterViewInit, OnDestroy {
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	@Input() mode: Mode = Mode.viewOnly;
	@Input() deviceGroupId = '';
	@Input() deviceGroupName = '';

	displayedColumns: string[] = [
		'rowSelection',
		'serialNumber',
		'deviceLocation',
		'propertyName',
		'companyName',
		'displayStatus',
		'rowMenu',
	];
	dataSource = new MatTableDataSource<{
		device: Device;
		groupNames?: string[];
	}>();

	filterLabels = {
		defaultPlaceholder: $localize`:@@deviceGroupDevices-defaultPlaceholder:Search serial number...`,
		serialNumber: $localize`:@@deviceGroupDevices-filterLabelSerialNumber:Serial Number`,
		source: $localize`:@@deviceGroupDevices-filterLabelSource:Source`,
		deviceLocation: $localize`:@@deviceGroupDevices-filterLabelDeviceLocation:Device Location`,
		propertyName: $localize`:@@deviceGroupDevices-filterLabelPropertyName:Property Name`,
		displayStatus: $localize`:@@deviceGroupDevices-filterLabelDisplayStatus:Display Status`,
		address: $localize`:@@deviceGroupDevices-filterLabelAddress:Address`,
		townCity: $localize`:@@deviceGroupDevices-filterLabelTownCity:Town/City`,
		stateProvince: $localize`:@@deviceGroupDevices-filterLabelStateProvince:State/Province`,
		country: $localize`:@@deviceGroupDevices-filterLabelCountry:Country`,
		postalCode: $localize`:@@deviceGroupDevices-filterLabelPostalCode:Postal Code`,
	};

	private filterSubscription?: Subscription;

	private hasDeviceManagementSystemCapability = false;
	private isDeviceGroupOwned = false;

	get canEditDevices(): boolean {
		return this.hasDeviceManagementSystemCapability;
	}

	@ViewChild(MatSort, { static: false }) sort!: MatSort;
	@ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

	get selectedDevices(): Device[] {
		return this.dataSet.selection.selected.map((it) => it.device);
	}

	getDevicesByDeviceGroupIdLoadingKey =
		ApiLoadingKeys.getDevicesByDeviceGroupId;
	finishedLoadingInitData: boolean = false;
	isCheckingDeviceGroupOwnership: boolean = true;

	constructor(
		private readonly apiService: ApiService,
		private readonly userSessionService: UserSessionService,
		private readonly dialog: MatDialog,
		private readonly router: Router,
		private readonly scService: SystemCapabilityService,
		private readonly deviceDetailsService: DeviceDetailsService,
		public readonly loadingService: LoadingIndicatorService,
		@Inject(ENV) public env: Environment
	) {
		this.hasDeviceManagementSystemCapability = this.scService.has(
			SystemCapabilityEnum.deviceManagement
		);
	}

	ngAfterViewInit(): void {
		this.checkDeviceGroupOwnership();

		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					// we only want to return devices that are still in the Device Group
					filter.push({
						param: 'deviceGroupDeviceStatus',
						value: ['active', 'upcoming'],
					});
					const hasRowIndex = filter.some((f) => f.param === 'rowIndex');
					const hasRowCount = filter.some((f) => f.param === 'rowCount');
					if (!hasRowIndex) {
						filter.push({ param: 'rowIndex', value: 0 });
					}
					if (!hasRowCount) {
						filter.push({ param: 'rowCount', value: 31 });
					}
					const result = (
						await this.apiService.getDevicesByDeviceGroupId(
							this.deviceGroupId,
							filter
						)
					).data;
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((data) => {
				this.dataSource.data = data;
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	onRowClick(element: {
		device: Device;
		deviceGroupDeviceStatus: DeviceGroupDeviceStatus;
	}): void {
		this.deviceDetailsService.saveDeviceDetails(element.device);
		this.router.navigate(['/devices', element.device.id]);
	}

	onAddButtonClick(): void {
		if (
			this.isCheckingDeviceGroupOwnership ||
			!this.checkDeviceGroupModifyPermission()
		) {
			return;
		}
		this.router.navigate(['/deviceGroup', this.deviceGroupId, 'add'], {
			state: { deviceGroupName: this.deviceGroupName },
		});
	}

	async checkDeviceGroupOwnership(): Promise<void> {
		if (!this.deviceGroupId) {
			// Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
			setTimeout(() => {
				this.isCheckingDeviceGroupOwnership = false;
			});
			return;
		}
		try {
			this.isCheckingDeviceGroupOwnership = true;
			const deviceGroup = await this.apiService.getDeviceGroupById(
				this.deviceGroupId
			);
			if (deviceGroup) {
				const companyId =
					this.userSessionService.userSession.assignedRole?.entityRelationship
						?.parentEntityId;
				this.isDeviceGroupOwned = deviceGroup.companyId === companyId;
			} else {
				this.isDeviceGroupOwned = false;
			}
		} catch (error) {
			this.isDeviceGroupOwned = false;
		} finally {
			// Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
			setTimeout(() => {
				this.isCheckingDeviceGroupOwnership = false;
			});
		}
	}

	checkDeviceGroupModifyPermission(): boolean {
		if (!this.isDeviceGroupOwned) {
			this.showPermissionError();
			return false;
		}
		return true;
	}

	showPermissionError(): void {
		this.dialog.open(CancelConfirmationComponent, {
			maxWidth: '400px',
			panelClass: 'styled-dialog-container',
			data: {
				dialogHeader: $localize`:@@deviceGroupDevices-cannotModifyGroupErrorHeader:Cannot Modify Group`,
				dialogContent: $localize`:@@deviceGroupDevices-permissionErrorMessage:You don't have permission to modify this device group.`,
				dialogPrimaryButtonText: $localize`:@@deviceGroupDevices-permissionErrorOk:OK`,
				dialogSecondaryButtonText: '',
				useSmallButtons: true,
				useCompactStyle: true,
				showDevider: true,
			},
		});
	}

	removeDevicesFromGroup(devices: Device[]): void {
		if (!this.checkDeviceGroupModifyPermission()) {
			return;
		}

		const dialogRef: MatDialogRef<RemoveDeviceAlertDialogComponent> =
			this.dialog.open(RemoveDeviceAlertDialogComponent, {
				maxWidth: '344px',
				data: {
					deviceCount: devices.length,
					groupName: this.deviceGroupName,
				},
			});
		dialogRef.afterClosed().subscribe((result: any) => {
			if (!!result) {
				const deviceIds = devices.map((it) => it.id);
				this.apiService
					.updateDeviceGroupMembership(this.deviceGroupId, deviceIds, 'remove')
					.then((_) => {
						this.dataSet.clearAllSelections();
						this.dataSet.applyFilters();
					});
			}
		});
	}

	getDeviceStatuses(row: {
		device: Device;
		deviceGroupDeviceStatus: DeviceGroupDeviceStatus;
	}): StatusDevice {
		return {
			deviceGroupDeviceStatus: row.deviceGroupDeviceStatus,
			groupStatus: row.device.groupStatus,
			status: row.device.status,
		};
	}

	isOwnedDeviceGroup(source: Company): boolean {
		const companyId =
			this.userSessionService.userSession.assignedRole?.entityRelationship
				?.parentEntityId;
		return source?.id === companyId;
	}

	rowId(row: { device: Device; groupNames?: string[] }): string {
		return row?.device?.id || 'unknown-id';
	}
}

export interface RemoveDeviceDialogData {
	deviceCount: number;
	groupName: string;
}

@Component({
	selector: 'app-remove-device-alert-dialog',
	template: ` <div class="column">
		<div class="row spacing-16">
			<mat-icon svgIcon="sl-alert"></mat-icon>
			<div class="mat-title remove-dialog-title">Confirm</div>
		</div>
		<div class="remove-dialog-body">
			Please confirm you want to remove ({{ data.deviceCount }})
			<ng-container [ngPlural]="data.deviceCount">
				<ng-template ngPluralCase="=1">device</ng-template>
				<ng-template ngPluralCase="other">devices</ng-template>
			</ng-container>
			from {{ data.groupName }}.
		</div>
		<div class="row-center">
			<button
				mat-button
				class="secondary-button"
				(click)="onCancelClicked()"
				data-automation-id="device-group-devices-cancel-button"
			>
				Cancel
			</button>
			<button
				mat-button
				class="primary-button"
				(click)="onRemoveClicked()"
				data-automation-id="device-group-devices-remove-button"
			>
				Remove
			</button>
		</div>
	</div>`,
	styleUrls: [
		'./device-group-devices.component.scss',
		'../../common/main.scss',
	],
	imports: [MatIcon, NgPlural, NgPluralCase, MatButton],
})
export class RemoveDeviceAlertDialogComponent {
	constructor(
		private dialogRef: MatDialogRef<RemoveDeviceAlertDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: RemoveDeviceDialogData
	) {}

	onCancelClicked(): void {
		this.dialogRef.close();
	}

	onRemoveClicked(): void {
		this.dialogRef.close({});
	}
}
